import React, { useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import uniqBy from 'lodash/uniqBy';
import styled, { css } from 'styled-components';
import { Text } from '@sparknz/spark-pattern-library';
import { LinkRegion } from '@sparknz/tracking';

import { useProductDetails } from 'journey/productDetails/provider';
import { ProductDetailsActionTypes } from 'journey/productDetails/reducer/productDetails.reducer';
import { DeviceOfferDetails } from 'journey/productDetails/typings';
import { getSelectedDevice } from 'journey/productDetails/utils/device.utils';
import ColorSelector from 'shared/components/ColorSelector';
import { useQuery } from 'shared/hooks';
import { getStorageOptionsByColor } from '../CapacityOptions/utils/storageOptions.utils';

export const generateColorOptions = (offers: DeviceOfferDetails) => {
  return uniqBy(offers, 'colorHex')
    .filter((offer) => offer?.color && offer?.colorHex)
    .map(({ color, colorHex }) => ({
      label: color,
      value: colorHex,
    }));
};

const SelectedColorText = styled(Text)`
  ${({ theme: { media } }) => css`
    text-align: center;

    ${media.up('slim')} {
      text-align: left;
    }
  `}
`;

const ColorOptions = () => {
  const { query, replaceQuery } = useQuery();
  const { dispatch, deviceOffers, selectedDevice, selectedStorage } =
    useProductDetails();

  const [selectedColor, setSelectedColor] = useState<string>();

  const colorOptions = generateColorOptions(deviceOffers);

  if (isEmpty(colorOptions)) {
    return null;
  }

  const handleSelect = (colorName: string, colorHex: string) => {
    setSelectedColor(colorHex);
    const { newSelectedStorage, newStorageOptions } = getStorageOptionsByColor(
      deviceOffers,
      colorHex,
      selectedStorage || selectedDevice.storage,
    );

    const newSelectedDevice = getSelectedDevice(
      deviceOffers,
      colorHex,
      newSelectedStorage,
    );

    dispatch({
      type: ProductDetailsActionTypes.SelectColor,
      selectedColor: colorHex,
      selectedStorage: newSelectedStorage,
      storageOptions: newStorageOptions,
    });

    replaceQuery({ ...query, offerId: newSelectedDevice.id });
  };

  return (
    <LinkRegion regionId="chooseColourTab">
      <SelectedColorText variant="subheadingM" mb="s2">
        {`Colour: ${
          colorOptions.find((option) => option.value === selectedColor)
            ?.label || selectedDevice.color
        }`}
      </SelectedColorText>
      <ColorSelector
        options={colorOptions}
        onChange={handleSelect}
        selectedValue={selectedColor || selectedDevice.colorHex}
      />
    </LinkRegion>
  );
};

export default ColorOptions;
